import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import dynamicfunction from '../../../utils/dynamicfunction';
import { cn, convertIntoArray, getContentKeyAccToLang } from '../../../utils/utils';
import { Checkbox, Chips, ErrorText, RadioButton, RowView, Text } from '../../CorporateForm/components';
import Button from '../../../components/Button';
import Dropdown from '../../../components/Dropdown';
import CustomInput from '../../../components/CustomInput';
import { useNavigate } from 'react-router';
import { logoutUser } from '../../../store/actions/authActions';
import { fontSizes } from '../../../constants/constants';
import {
  getIndBankInformationData,
  getIndContactInformationData,
  getIndInvestmentPortfolioData,
  postIndInvestmentPortfolio,
  saveIndKycInvestmentPortfolio,
} from '../../../store/actions/individualAction';
import individual_investment_portfolio_validation_schema from '../../../validations_schemas/individual_investment_portfolio_validation_schema';
import KeepValuesOnLanguageChange from '../../../components/KeepValuesOnLanguageChange';

const InvestmentInformation = ({ onDone }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isArabic, TextWeight } = dynamicfunction();

  const { individualContent, language } = useSelector((state) => state.content);
  const {
    custodian_detail_dropdowns,
    ind_post_loading,
    ind_investment_portfolio_data,
    ind_investment_portfolio_dropdown_data,
    ind_contact_information_dropdown_data,
    ind_bank_information_dropdown_data,
  } = useSelector((state) => state.individual);

  const { client_application_id, is_kyc } = useSelector((state) => state.constant);

  // Unsaved form values captured on language switch; in memory only, so a hard reload clears them.
  const draftValues = useRef(null);

  const getContent = (key) => {
    return getContentKeyAccToLang(individualContent?.investment_portfolio?.data?.attributes, key, isArabic);
  };

  const myArray = convertIntoArray(ind_investment_portfolio_dropdown_data);
  const languageArray = convertIntoArray(ind_contact_information_dropdown_data);
  const custodianArray = convertIntoArray(ind_bank_information_dropdown_data);

  useEffect(() => {
    getData();
  }, [language]);

  const getData = async () => {
    await dispatch(getIndInvestmentPortfolioData(client_application_id));
    await dispatch(getIndContactInformationData(client_application_id));
    await dispatch(getIndBankInformationData(client_application_id));
  }

  const onSubmit = (values) => {
    const api_data = {
      ...ind_investment_portfolio_data,
      sourceOfFundForThisAccount: values?.sourceOfFundForThisAccount,
      haveYouWorkedInTheFinancialSectorInThePastFiveYears: values?.haveYouWorkedInTheFinancialSectorInThePastFiveYears === '1' ? true : false,
      doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector:
        values?.doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector === '1' ? true : false,
      doYouHaveAnExistingPortfolio: values?.doYouHaveAnExistingPortfolio === '1' ? true : false,
      whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId:
        values?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId ?? null,
      investmentKnowledgeAndExperienceId: values?.investmentKnowledgeAndExperienceId ?? null,
      whatIsYourRiskAppetiteId: values?.whatIsYourRiskAppetiteId ?? null,
      forHowManyYearsHaveYouInvestedFininancialSecuritiesId: Number(values?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId?.id) ?? null,
      productsPreviouslyInvestedIn: values?.productsPreviouslyInvestedIn ?? null,
      professionalCertificates: values?.professionalCertificates ?? null,
      loanToInvestedMoneyRatio: values?.loanToInvestedMoneyRatio,
      marginTransactionOverThePastFiveYears: values?.marginTransactionOverThePastFiveYears,
      haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia: values?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia === '1' ? true : false,
      ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes:
        values?.ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes,
      whatIsYourGeneraInvestmentObjectiveId: values?.whatIsYourGeneraInvestmentObjectiveId,
      preferredInvestmentCurrencies: values?.preferredInvestmentCurrencies?.map((val) => val?.valueId) ?? [],
      foreignCurrencyId: values?.foreignCurrencyId?.map((val) => Number(val.id)) ?? [],
      whatIsYourExpectedTimeHorizonForRedeemingYourInvestment: Number(values?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment?.id) ?? null,
      doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions:
        values?.doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions === '1' ? true : false,
      toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId:
        values?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId ?? null,
      preferredLanguageForCorrespondenceId: values?.preferredLanguageForCorrespondenceId ?? null,

      certificateId: values?.certificateId,
      certificateOther: values?.certificateOther,
      divedendId: values?.divedendId,
      divedendOther: values?.divedendOther,
      salesProceedId: values?.salesProceedId,
      salesProceedOther: values?.salesProceedOther,
      anyOtherInformationOnTheClientFinancialSituation: values?.anyOtherInformationOnTheClientFinancialSituation,

      // Client Keys
      targetInvestmentMoneyMarketMurabaha: Number(values?.targetInvestmentMoneyMarketMurabaha) || 0,
      targetInvestmentDebtInsruments: Number(values.targetInvestmentDebtInsruments) || 0,
      targetInvestmentStocks: Number(values?.targetInvestmentStocks) || 0,
      targetInvestmentRealEstate: Number(values?.targetInvestmentRealEstate) || 0,
      targetInvestmentDerivativesContracts: Number(values?.targetInvestmentDerivativesContracts) || 0,
      targetInvestmentAlternativeInvestments: Number(values?.targetInvestmentAlternativeInvestments) || 0,
      targetInvestmentInvestmentFunds: Number(values?.targetInvestmentInvestmentFunds) || 0,

      // Current Keys
      existingInvestmentMoneyMarketMurabaha: Number(values?.existingInvestmentMoneyMarketMurabaha) || 0,
      existingInvestmentDebtInsruments: Number(values?.existingInvestmentDebtInsruments) || 0,
      existingInvestmentStocks: Number(values?.existingInvestmentStocks) || 0,
      existingInvestmentRealEstate: Number(values?.existingInvestmentRealEstate) || 0,
      existingInvestmentDerivativesContracts: Number(values?.existingInvestmentDerivativesContracts) || 0,
      existingInvestmentAlternativeInvestments: Number(values?.existingInvestmentAlternativeInvestments) || 0,
      existingInvestmentInvestmentFunds: Number(values?.existingInvestmentInvestmentFunds) || 0,
    };

    dispatch(
      is_kyc
        ? saveIndKycInvestmentPortfolio(api_data, (res) => {
          values?.isSaveExist ? navigate('/funds/available-funds') : onDone();
        })
        : postIndInvestmentPortfolio(api_data, (res) => {
          values?.isSaveExist ? dispatch(logoutUser()) : onDone();
        }),
    );
  };

  const portfolioData = [
    {
      name: getContent('deposits_murabaha'),
      clientKey: 'targetInvestmentMoneyMarketMurabaha',
      currentKey: 'existingInvestmentMoneyMarketMurabaha',
    },
    {
      name: getContent('debt_instruments'),
      clientKey: 'targetInvestmentDebtInsruments',
      currentKey: 'existingInvestmentDebtInsruments',
    },
    {
      name: getContent('stocks'),
      clientKey: 'targetInvestmentStocks',
      currentKey: 'existingInvestmentStocks',
    },
    {
      name: getContent('real_estate'),
      clientKey: 'targetInvestmentRealEstate',
      currentKey: 'existingInvestmentRealEstate',
    },
    {
      name: getContent('derivatives_contracts'),
      clientKey: 'targetInvestmentDerivativesContracts',
      currentKey: 'existingInvestmentDerivativesContracts',
    },
    {
      name: getContent('alternative_investments'),
      clientKey: 'targetInvestmentAlternativeInvestments',
      currentKey: 'existingInvestmentAlternativeInvestments',
    },
    {
      name: getContent('investment_funds'),
      clientKey: 'targetInvestmentInvestmentFunds',
      currentKey: 'existingInvestmentInvestmentFunds',
    },
  ];

  const optionsYesOrNO = [
    { id: '1', name: getContent('yes') },
    { id: '0', name: getContent('no') },
  ];

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={draftValues.current ? {
          ...draftValues.current,
          // Re-pick dropdown objects from the refetched list so labels show in the new language.
          forHowManyYearsHaveYouInvestedFininancialSecuritiesId:
            myArray?.forHowManyYearsHaveYouInvestedFininancialSecuritiesList?.find(
              (item) => item.id === String(draftValues.current?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId?.id),
            ) || null,
          preferredInvestmentCurrencies:
            draftValues.current?.preferredInvestmentCurrencies?.map((val) => ({
              ...val,
              otherTypeOfInvestment:
                myArray?.preferredInvestmentCurrenciesList?.find((item) => item.id === val?.valueId)?.name ?? val?.otherTypeOfInvestment,
            })) ?? [],
          foreignCurrencyId:
            myArray?.currencyList
              ?.filter((item) => draftValues.current?.foreignCurrencyId?.some((val) => String(val?.id) === item.id))
              ?.map((item) => ({
                id: item.id,
                name: item.name,
              })) ?? [],
          whatIsYourExpectedTimeHorizonForRedeemingYourInvestment:
            myArray?.expectedTimeHorizonForRedeemingYourInvestment?.find(
              (item) => item.id === String(draftValues.current?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment?.id),
            ) || null,
        } : {
          sourceOfFundForThisAccount: ind_investment_portfolio_data?.sourceOfFundForThisAccount?.map((item) => item?.toString()) ?? [],
          haveYouWorkedInTheFinancialSectorInThePastFiveYears: ind_investment_portfolio_data?.haveYouWorkedInTheFinancialSectorInThePastFiveYears
            ? 1
            : 0,
          doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector:
            ind_investment_portfolio_data?.doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector ? 1 : 0,
          doYouHaveAnExistingPortfolio: ind_investment_portfolio_data?.doYouHaveAnExistingPortfolio ? 1 : 0,
          whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId:
            ind_investment_portfolio_data?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId?.toString() ??
            '',
          investmentKnowledgeAndExperienceId: ind_investment_portfolio_data?.investmentKnowledgeAndExperienceId?.toString() ?? '',
          whatIsYourRiskAppetiteId: ind_investment_portfolio_data?.whatIsYourRiskAppetiteId?.toString() ?? '',
          forHowManyYearsHaveYouInvestedFininancialSecuritiesId:
            myArray?.forHowManyYearsHaveYouInvestedFininancialSecuritiesList?.find(
              (item) => item.id === String(ind_investment_portfolio_data?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId),
            ) || null,
          productsPreviouslyInvestedIn: ind_investment_portfolio_data?.productsPreviouslyInvestedIn ?? '',
          professionalCertificates: ind_investment_portfolio_data?.professionalCertificates ?? '',
          loanToInvestedMoneyRatio: ind_investment_portfolio_data?.loanToInvestedMoneyRatio ?? '',
          marginTransactionOverThePastFiveYears: ind_investment_portfolio_data?.marginTransactionOverThePastFiveYears ?? '',
          haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia:
            ind_investment_portfolio_data?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia ? '1' : '0',
          ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes:
            ind_investment_portfolio_data?.ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes ?? '',
          whatIsYourGeneraInvestmentObjectiveId:
            ind_investment_portfolio_data?.whatIsYourGeneraInvestmentObjectiveId?.map((item) => item?.toString()) ?? [],
          preferredInvestmentCurrencies:
            myArray?.preferredInvestmentCurrenciesList
              ?.filter((item) => ind_investment_portfolio_data?.preferredInvestmentCurrencies?.includes(Number(item.id)))
              ?.map((item) => ({
                typeOfInvestmentId: 0,
                valueId: item.id,
                otherTypeOfInvestment: item.name,
              })) ?? [],
          foreignCurrencyId:
            myArray?.currencyList
              ?.filter((item) => ind_investment_portfolio_data?.foreignCurrencyId?.includes(Number(item.id)))
              ?.map((item) => ({
                id: item.id,
                name: item.name,
              })) ?? [],
          whatIsYourExpectedTimeHorizonForRedeemingYourInvestment:
            myArray?.expectedTimeHorizonForRedeemingYourInvestment?.find(
              (item) => item.id === String(ind_investment_portfolio_data?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment),
            ) || null,
          doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions:
            ind_investment_portfolio_data?.doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions ? '1' : '0',
          toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId:
            ind_investment_portfolio_data?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId?.toString() ?? '',
          preferredLanguageForCorrespondenceId: ind_investment_portfolio_data?.preferredLanguageForCorrespondenceId?.toString() ?? '',
          certificateId: ind_investment_portfolio_data?.certificateId ?? '',
          certificateOther: ind_investment_portfolio_data?.certificateOther ?? '',
          divedendId: ind_investment_portfolio_data?.divedendId ?? '',
          divedendOther: ind_investment_portfolio_data?.divedendOther ?? '',
          salesProceedId: ind_investment_portfolio_data?.salesProceedId ?? '',
          salesProceedOther: ind_investment_portfolio_data?.salesProceedOther ?? '',
          doYouHaveAnExistingPortfolio: ind_investment_portfolio_data?.doYouHaveAnExistingPortfolio ? '1' : '0',
          anyOtherInformationOnTheClientFinancialSituation: ind_investment_portfolio_data?.anyOtherInformationOnTheClientFinancialSituation ?? '',

          // Client Keys
          targetInvestmentMoneyMarketMurabaha: ind_investment_portfolio_data?.targetInvestmentMoneyMarketMurabaha ?? '',
          targetInvestmentDebtInsruments: ind_investment_portfolio_data?.targetInvestmentDebtInsruments ?? '',
          targetInvestmentStocks: ind_investment_portfolio_data?.targetInvestmentStocks ?? '',
          targetInvestmentRealEstate: ind_investment_portfolio_data?.targetInvestmentRealEstate ?? '',
          targetInvestmentDerivativesContracts: ind_investment_portfolio_data?.targetInvestmentDerivativesContracts ?? '',
          targetInvestmentAlternativeInvestments: ind_investment_portfolio_data?.targetInvestmentAlternativeInvestments ?? '',
          targetInvestmentInvestmentFunds: ind_investment_portfolio_data?.targetInvestmentInvestmentFunds ?? '',

          // Current Keys
          existingInvestmentMoneyMarketMurabaha: ind_investment_portfolio_data?.existingInvestmentMoneyMarketMurabaha ?? '',
          existingInvestmentDebtInsruments: ind_investment_portfolio_data?.existingInvestmentDebtInsruments ?? '',
          existingInvestmentStocks: ind_investment_portfolio_data?.existingInvestmentStocks ?? '',
          existingInvestmentRealEstate: ind_investment_portfolio_data?.existingInvestmentRealEstate ?? '',
          existingInvestmentDerivativesContracts: ind_investment_portfolio_data?.existingInvestmentDerivativesContracts ?? '',
          existingInvestmentAlternativeInvestments: ind_investment_portfolio_data?.existingInvestmentAlternativeInvestments ?? '',
          existingInvestmentInvestmentFunds: ind_investment_portfolio_data?.existingInvestmentInvestmentFunds ?? '',
          isSaveExist: false,
        }}
        onSubmit={(values) => {
          onSubmit(values);
        }}
        validationSchema={individual_investment_portfolio_validation_schema}
        validateOnChange={true}
        validateOnBlur={true}
      >
        {({ handleSubmit, setFieldValue, values, errors, touched, setFieldTouched }) => {
          const totalIdeal =
            Number(values?.targetInvestmentMoneyMarketMurabaha) +
            Number(values?.targetInvestmentDebtInsruments) +
            Number(values?.targetInvestmentStocks) +
            Number(values?.targetInvestmentRealEstate) +
            Number(values?.targetInvestmentDerivativesContracts) +
            Number(values?.targetInvestmentAlternativeInvestments) +
            Number(values?.targetInvestmentInvestmentFunds);

          const totalCurrent =
            Number(values?.existingInvestmentMoneyMarketMurabaha) +
            Number(values?.existingInvestmentDebtInsruments) +
            Number(values?.existingInvestmentStocks) +
            Number(values?.existingInvestmentRealEstate) +
            Number(values?.existingInvestmentDerivativesContracts) +
            Number(values?.existingInvestmentAlternativeInvestments) +
            Number(values?.existingInvestmentInvestmentFunds);

          return (
            <div className=''>
              <KeepValuesOnLanguageChange onLanguageChange={(currentValues) => (draftValues.current = currentValues)} />
              <RowView className={'flex-col'}>
                <div className='mb-3 sm:mb-5'>
                  <Text text={getContent('source_of_income')} weight={TextWeight.semiBold} size={fontSizes.h22} className={'block'} />
                </div>
                <Chips
                  isMulti
                  label={getContent('source_of_income')}
                  isRequired
                  value={values?.sourceOfFundForThisAccount}
                  onChange={(e) => {
                    setFieldValue('sourceOfFundForThisAccount', e);
                    setFieldTouched('sourceOfFundForThisAccount', false);
                  }}
                  options={myArray?.sourceOfIncomeList}
                />
                <ErrorText text={getContent(touched?.sourceOfFundForThisAccount ? errors?.sourceOfFundForThisAccount : '')} />
              </RowView>
              <RowView className='pt-3 sm:pt-5 flex-col'>
                <div className='mb-3 sm:mb-5'>
                  <Text
                    text={getContent('client_professional_experience_finalcial_sector')}
                    isTruncate={false}
                    weight={TextWeight.semiBold}
                    size={fontSizes.h22}
                    className={'block'}
                  />
                </div>
                <RadioButton
                  label={getContent('worked_finalcial_sector_five_years')}
                  required={false}
                  value={values?.haveYouWorkedInTheFinancialSectorInThePastFiveYears}
                  onChange={(e) => {
                    setFieldValue('haveYouWorkedInTheFinancialSectorInThePastFiveYears', e);
                    setFieldTouched('haveYouWorkedInTheFinancialSectorInThePastFiveYears', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText
                  text={getContent(
                    touched?.haveYouWorkedInTheFinancialSectorInThePastFiveYears ? errors?.haveYouWorkedInTheFinancialSectorInThePastFiveYears : '',
                  )}
                />
              </RowView>
              <RowView className='mt-3 sm:mt-5' isResponsive>
                <RadioButton
                  label={getContent('is_practical_experience')}
                  required={false}
                  value={values?.doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector}
                  onChange={(e) => {
                    setFieldValue('doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector', e);
                    setFieldTouched('doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText
                  text={getContent(
                    touched?.doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector
                      ? errors?.doYouHaveAnyOthePracticalExperienceRelatedToTheFinancialSector
                      : '',
                  )}
                />
              </RowView>
              <div className='mt-3 sm:mt-5' isResponsive>
                <Text
                  text={getContent('investment_information')}
                  isTruncate={false}
                  weight={TextWeight.semiBold}
                  size={fontSizes.h22}
                  className={'block'}
                />
              </div>
              <RowView className='flex-col mt-3 sm:mt-5'>
                <RadioButton
                  label={getContent('is_existing_portfolio')}
                  required={false}
                  value={values?.doYouHaveAnExistingPortfolio}
                  onChange={(e) => {
                    setFieldValue('doYouHaveAnExistingPortfolio', e);
                    setFieldTouched('doYouHaveAnExistingPortfolio', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText text={getContent(touched?.doYouHaveAnExistingPortfolio ? errors?.doYouHaveAnExistingPortfolio : '')} />
              </RowView>
              <RowView className={'flex-col mt-3 sm:mt-5'}>
                <Chips
                  label={getContent('overall_investment_percentage')}
                  value={values?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId}
                  onChange={(e) => {
                    setFieldValue('whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId', e);
                    setFieldTouched('whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId', false);
                  }}
                  options={myArray?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsList}
                />
                <ErrorText
                  text={getContent(
                    touched?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId
                      ? errors?.whatDoesYourOverallLocalAndInternationalInvestmentsRepresentAsAPercentageOfYourTotalNetAssetsId
                      : '',
                  )}
                />
              </RowView>

              <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                <div className='w-full'>
                  <Chips
                    label={getContent('investment_knowledge_experience')}
                    value={values?.investmentKnowledgeAndExperienceId}
                    onChange={(e) => {
                      setFieldValue('investmentKnowledgeAndExperienceId', e);
                      setFieldTouched('investmentKnowledgeAndExperienceId', false);
                    }}
                    options={myArray?.investmentKnowledgeAndExperienceList}
                  />
                  <ErrorText text={getContent(touched?.investmentKnowledgeAndExperienceId ? errors?.investmentKnowledgeAndExperienceId : '')} />
                </div>
                <div className='w-full flex flex-col'>
                  <Chips
                    label={getContent('client_appetite_risk')}
                    value={values?.whatIsYourRiskAppetiteId}
                    onChange={(e) => {
                      setFieldValue('whatIsYourRiskAppetiteId', e);
                      setFieldTouched('whatIsYourRiskAppetiteId', false);
                    }}
                    options={myArray?.riskAppetite}
                  />
                  <ErrorText text={getContent(touched?.whatIsYourRiskAppetiteId ? errors?.whatIsYourRiskAppetiteId : '')} />
                </div>
                <div className='lg:w-full hidden lg:block'></div>
              </RowView>
              <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                <Dropdown
                  isRequired
                  label={getContent('years_investment_securities')}
                  placeholder={getContent('please_select')}
                  options={myArray?.forHowManyYearsHaveYouInvestedFininancialSecuritiesList}
                  selectedValue={values?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId}
                  valueKey='name' // id
                  onSelect={(val) => {
                    setFieldValue('forHowManyYearsHaveYouInvestedFininancialSecuritiesId', val);
                    setFieldTouched('forHowManyYearsHaveYouInvestedFininancialSecuritiesId', false);
                  }}
                  labelDivClass={'h-10 sm:h-6 md:h-9 xl:h-6'}
                  errorMessage={getContent(
                    touched?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId
                      ? errors?.forHowManyYearsHaveYouInvestedFininancialSecuritiesId
                      : '',
                  )}
                />
                <CustomInput
                  isRounded
                  isRequired
                  label={getContent('products_previously_invested')}
                  placeholder={getContent('please_specify')}
                  value={values?.productsPreviouslyInvestedIn}
                  onChange={(e) => {
                    setFieldValue('productsPreviouslyInvestedIn', e.target.value);
                    setFieldTouched('productsPreviouslyInvestedIn', true, false);
                  }}
                  labelDivClass={'h-10 sm:h-6 md:h-9 xl:h-6'}
                  errorMessage={getContent(touched?.productsPreviouslyInvestedIn ? errors?.productsPreviouslyInvestedIn : '')}
                />
                <CustomInput
                  isRounded
                  isRequired
                  label={getContent('professional_certificates')}
                  placeholder={getContent('please_specify')}
                  value={values?.professionalCertificates}
                  onChange={(e) => {
                    setFieldValue('professionalCertificates', e.target.value);
                    setFieldTouched('professionalCertificates', true, false);
                  }}
                  labelDivClass={'h-6 md:h-9 xl:h-6'}
                  errorMessage={getContent(touched?.professionalCertificates ? errors?.professionalCertificates : '')}
                />
              </RowView>
              <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                <CustomInput
                  isRounded
                  isRequired
                  label={getContent('loan_invested_money')}
                  placeholder={getContent('please_specify')}
                  value={values?.loanToInvestedMoneyRatio}
                  onChange={(e) => {
                    setFieldValue('loanToInvestedMoneyRatio', e.target.value);
                    setFieldTouched('loanToInvestedMoneyRatio', true, false);
                  }}
                  labelDivClass={'h-6 md:h-14 lg:h-10 xl:h-7'}
                  errorMessage={getContent(touched?.loanToInvestedMoneyRatio ? errors?.loanToInvestedMoneyRatio : '')}
                />
                <CustomInput
                  isRounded
                  isRequired
                  label={getContent('margin_transactions_past_five_years')}
                  placeholder={getContent('please_specify')}
                  value={values?.marginTransactionOverThePastFiveYears}
                  onChange={(e) => {
                    setFieldValue('marginTransactionOverThePastFiveYears', e.target.value);
                    setFieldTouched('marginTransactionOverThePastFiveYears', true, false);
                  }}
                  labelDivClass={'h-10 sm:h-6 md:h-14 lg:h-10 xl:h-7'}
                  errorMessage={getContent(touched?.marginTransactionOverThePastFiveYears ? errors?.marginTransactionOverThePastFiveYears : '')}
                />
                <div className='w-full'>
                  <RadioButton
                    label={getContent('is_security_transaction_outside_kingdom')}
                    isRequired
                    value={values?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia}
                    onChange={(e) => {
                      setFieldValue('haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia', e);
                      setFieldTouched('haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia', false);
                    }}
                    labelDivClass={'h-14 sm:h-4 md:h-14 lg:h-10 xl:h-7'}
                    options={optionsYesOrNO}
                  />
                  <ErrorText
                    text={getContent(
                      touched?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia
                        ? errors?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia
                        : '',
                    )}
                  />
                </div>
              </RowView>
              {values?.haveYouInvestedInFinancialSecuritiesOutsideSaudiArabia?.toString() === '1' && (
                <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                  <CustomInput
                    isRounded
                    isRequired={false}
                    label={getContent('if_security_transaction_executed_last_five_years')}
                    placeholder={getContent('please_specify')}
                    value={values?.ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes}
                    onChange={(e) => {
                      setFieldValue('ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes', e.target.value);
                      setFieldTouched('ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes', true, false);
                    }}
                    labelDivClass={'h-22 sm:h-10 md:h-9 xl:h-6'}
                    errorMessage={getContent(
                      touched?.ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes
                        ? errors?.ifSecuritiesTransactionWereExecutedOutsideInWhichCountriesTransactionsExecutes
                        : '',
                    )}
                  />
                </RowView>
              )}

              <RowView className={'flex-col mt-3 sm:mt-5'}>
                <Chips
                  isMulti
                  required={false}
                  label={getContent('general_investment_objectives')}
                  value={values?.whatIsYourGeneraInvestmentObjectiveId}
                  onChange={(e) => {
                    setFieldValue('whatIsYourGeneraInvestmentObjectiveId', e);
                    setFieldTouched('whatIsYourGeneraInvestmentObjectiveId', false);
                  }}
                  options={myArray?.generalInvestmentObjective}
                />
                <ErrorText text={getContent(touched?.whatIsYourGeneraInvestmentObjectiveId ? errors?.whatIsYourGeneraInvestmentObjectiveId : '')} />
              </RowView>
              <div className='pt-3 sm:pt-5' isResponsive>
                <Checkbox
                  label={getContent('preferred_investment_assets')}
                  value={values?.preferredInvestmentCurrencies}
                  onChange={(option) => {
                    let updated_arr = [...(values?.preferredInvestmentCurrencies || [])];
                    const exists = updated_arr.some((item) => item?.valueId === option.id);

                    if (exists) {
                      updated_arr = updated_arr.filter((item) => item.valueId !== option.id);
                    } else {
                      updated_arr.push({
                        typeOfInvestmentId: 0,
                        valueId: option?.id,
                        otherTypeOfInvestment: option?.name,
                      });
                    }
                    setFieldValue('preferredInvestmentCurrencies', updated_arr);
                    setFieldTouched('preferredInvestmentCurrencies', false);
                  }}
                  options={myArray?.preferredInvestmentCurrenciesList}
                  isVertical
                  isRequired
                />
                <ErrorText text={getContent(touched?.preferredInvestmentCurrencies ? errors?.preferredInvestmentCurrencies : '')} />
                {values?.preferredInvestmentCurrencies?.some((item) => item.valueId === '790') && (
                  <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                    <Dropdown
                      isRequired
                      isMulti
                      label={getContent('state_foreign_currencies')}
                      placeholder={getContent('please_select')}
                      options={myArray?.currencyList}
                      selectedValue={values?.foreignCurrencyId}
                      valueKey='name'
                      onSelect={(val) => {
                        setFieldValue('foreignCurrencyId', val);
                        setFieldTouched('foreignCurrencyId', false);
                      }}
                      labelDivClass={'h-10 sm:h-6'}
                      errorMessage={getContent(touched?.foreignCurrencyId ? errors?.foreignCurrencyId : '')}
                    />
                  </RowView>
                )}
              </div>

              <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                <Dropdown
                  isRequired
                  label={getContent('period_client_expect_cash')}
                  placeholder={getContent('please_select')}
                  options={myArray?.expectedTimeHorizonForRedeemingYourInvestment}
                  selectedValue={values?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment}
                  valueKey='name' // id
                  onSelect={(val) => {
                    setFieldValue('whatIsYourExpectedTimeHorizonForRedeemingYourInvestment', val);
                    setFieldTouched('whatIsYourExpectedTimeHorizonForRedeemingYourInvestment', false);
                  }}
                  labelDivClass={'h-12 sm:h-6'}
                  errorMessage={getContent(
                    touched?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment
                      ? errors?.whatIsYourExpectedTimeHorizonForRedeemingYourInvestment
                      : '',
                  )}
                />
              </RowView>

              <div className='w-full mt-3 sm:mt-5' isResponsive>
                <RadioButton
                  label={getContent('have_understanding_for_independant_investment')}
                  required={true}
                  value={values?.doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions}
                  onChange={(e) => {
                    setFieldValue('doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions', e);
                    setFieldTouched('doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions', false);
                  }}
                  options={optionsYesOrNO}
                />
                <ErrorText
                  text={getContent(
                    touched?.doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions
                      ? errors?.doYouHaveSufficientUnderstandingOfFinancialMarketsToMakeIndependentInvestmentDecisions
                      : '',
                  )}
                />
              </div>
              <RowView className={'gap-7 mt-3 sm:mt-5'} isResponsive>
                <div className='w-full'>
                  <Chips
                    label={getContent('extent_advice_relied_upon')}
                    value={values?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId}
                    onChange={(e) => {
                      setFieldValue('toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId', e);
                      setFieldTouched('toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId', false);
                    }}
                    options={myArray?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponList}
                  />
                  <ErrorText
                    text={getContent(
                      touched?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId
                        ? errors?.toWhatExtentCanTheAdviceProvidedForThisInvestmentBeReliedUponId
                        : '',
                    )}
                  />
                </div>
                <div className='w-full flex flex-col'>
                  <Chips
                    label={getContent('correspondence_language')}
                    value={values?.preferredLanguageForCorrespondenceId}
                    onChange={(e) => {
                      setFieldValue('preferredLanguageForCorrespondenceId', e);
                      setFieldTouched('preferredLanguageForCorrespondenceId', false);
                    }}
                    options={languageArray?.preferredLanguageForCorrespondence}
                  />
                  <ErrorText text={getContent(touched?.preferredLanguageForCorrespondenceId ? errors?.preferredLanguageForCorrespondenceId : '')} />
                </div>
              </RowView>

              {/* <div className='mt-4' isResponsive>
                <RadioButton
                  label={getContent('have_existing_portfolio')}
                  value={values?.doYouHaveAnExistingPortfolio}
                  onChange={(e) => {
                    setFieldValue('doYouHaveAnExistingPortfolio', e);
                    setFieldTouched('doYouHaveAnExistingPortfolio', false);
                  }}
                  options={optionsYesOrNO}
                  required={false}
                />
                <ErrorText
                  text={getContent(
                    touched?.doYouHaveAnExistingPortfolio
                      ? errors?.doYouHaveAnExistingPortfolio
                      : ''
                  )}
                />
              </div> */}

              {values?.doYouHaveAnExistingPortfolio?.toString() === '1' && (
                <>
                  <div className='border border-border rounded-lg mt-4 w-full overflow-x-auto'>
                    <RowView className='bg-primary/10 px-4 py-3'>
                      <div className='flex-1' />
                      <RowView className='flex-1'>
                        <Text
                          text={getContent('ideal_investment_profile')}
                          color='text-primary'
                          weight={TextWeight.medium}
                          className='w-2/3 block'
                          isTruncate={false}
                        />
                      </RowView>
                      <RowView className='flex-1'>
                        <Text
                          text={getContent('current_investment_profile')}
                          color='text-primary'
                          weight={TextWeight.medium}
                          className='w-2/3 block'
                          isTruncate={false}
                        />
                      </RowView>
                    </RowView>
                    {portfolioData?.map((item, index) => (
                      <RowView className='px-4 py-3 border-b border-b-border' key={index}>
                        <Text text={item?.name} className='flex-1' />

                        <input
                          value={values?.[item?.clientKey]}
                          className={cn('outline-none flex-1', isArabic ? 'text-end' : 'text-start')}
                          placeholder='0'
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setFieldValue(item?.clientKey, val);
                            }
                          }}
                          maxLength={3}
                        />

                        <input
                          value={values?.[item?.currentKey]}
                          className={cn('outline-none flex-1', isArabic ? 'text-end' : 'text-start')}
                          placeholder='0'
                          onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                              setFieldValue(item?.currentKey, val);
                            }
                          }}
                          maxLength={3}
                        />
                      </RowView>
                    ))}

                    <RowView className='px-4 py-3 border-b border-b-border'>
                      <Text text={getContent('total')} className='flex-1' />
                      <Text text={`${totalIdeal ?? 0} %`} className='flex-1' />
                      <Text text={`${totalCurrent ?? 0} %`} className='flex-1' />
                    </RowView>
                  </div>
                  <ErrorText text={totalIdeal !== 100 || totalCurrent !== 100 ? getContent('total_portfolio_must_equal_100') : ''} />
                </>
              )}

              <div className='mt-5' isResponsive>
                <Text text={getContent('indicate_where_forward')} isTruncate={false} weight={TextWeight.medium} isRequired className={'block'} />
              </div>

              <RowView className='gap-7 mt-3 sm:mt-4' isResponsive>
                <div className='w-full'>
                  <RadioButton
                    label={getContent('certificate')}
                    value={values?.certificateId}
                    onChange={(e) => {
                      setFieldValue('certificateId', e);
                      setFieldValue(
                        'certificateIdCustodian',
                        custodian_detail_dropdowns?.dividendsRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('certificateId', false);
                    }}
                    options={custodianArray?.certificateRadioBtn}
                    required={false}
                  />
                  <ErrorText text={getContent(touched?.certificateId ? errors?.certificateId : '')} />
                  {Number(values?.certificateId) === 803 && (
                    <CustomInput
                      isRounded
                      maxLength={80}
                      placeholder={getContent('other_please_specify')}
                      value={values?.certificateOther}
                      onChange={(e) => {
                        setFieldValue('certificateOther', e.target.value);
                        setFieldTouched('certificateOther', true, false);
                      }}
                      errorMessage={getContent(touched?.certificateOther ? errors?.certificateOther : '')}
                    />
                  )}
                </div>
                <div className='w-full'>
                  <RadioButton
                    label={getContent('dividends_other_income')}
                    value={values?.divedendId}
                    onChange={(e) => {
                      setFieldValue('divedendId', e);
                      setFieldValue(
                        'divedendIdCustodian',
                        custodian_detail_dropdowns?.dividendsRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('divedendId', false);
                    }}
                    options={custodianArray?.dividendsRadioBtn}
                    required={false}
                  />
                  <ErrorText text={getContent(touched?.divedendId ? errors?.divedendId : '')} />

                  {Number(values?.divedendId) === 803 && (
                    <CustomInput
                      isRounded
                      maxLength={80}
                      placeholder={getContent('other_please_specify')}
                      value={values?.divedendOther}
                      onChange={(e) => {
                        setFieldValue('divedendOther', e.target.value);
                        setFieldTouched('divedendOther', true, false);
                      }}
                      errorMessage={getContent(touched?.divedendOther ? errors?.divedendOther : '')}
                    />
                  )}
                </div>
                <div className='w-full'>
                  <RadioButton
                    label={getContent('sales_proceeds')}
                    value={values?.salesProceedId}
                    onChange={(e) => {
                      setFieldValue('salesProceedId', e);
                      setFieldValue(
                        'salesProceedIdCustodian',
                        custodian_detail_dropdowns?.salesRadioBtn?.find((item) => item?.id?.toString() === e?.toString())?.name || '',
                      );
                      setFieldTouched('salesProceedId', false);
                    }}
                    options={custodianArray?.salesRadioBtn}
                    required={false}
                  />
                  <ErrorText text={getContent(touched?.salesProceedId ? errors?.salesProceedId : '')} />

                  {Number(values?.salesProceedId) === 803 && (
                    <CustomInput
                      isRounded
                      maxLength={80}
                      placeholder={getContent('other_please_specify')}
                      value={values?.salesProceedOther}
                      onChange={(e) => {
                        setFieldValue('salesProceedOther', e.target.value);
                        setFieldTouched('salesProceedOther', true, false);
                      }}
                      errorMessage={getContent(touched?.salesProceedOther ? errors?.salesProceedOther : '')}
                    />
                  )}
                </div>
              </RowView>

              <RowView className='gap-7 mt-3 sm:mt-5' isResponsive>
                <CustomInput
                  isRounded
                  isRequired={true}
                  maxLength={100}
                  label={getContent('other_financial_information')}
                  placeholder={getContent('please_specify')}
                  value={values?.anyOtherInformationOnTheClientFinancialSituation}
                  onChange={(e) => {
                    setFieldValue('anyOtherInformationOnTheClientFinancialSituation', e.target.value);
                    setFieldTouched('anyOtherInformationOnTheClientFinancialSituation', true, false);
                  }}
                  labelDivClass={'h-12 sm:h-6'}
                  errorMessage={getContent(
                    touched?.anyOtherInformationOnTheClientFinancialSituation ? errors?.anyOtherInformationOnTheClientFinancialSituation : '',
                  )}
                />
              </RowView>

              <div className='mt-5'>
                <Text text={getContent('financial_content_heading')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
              </div>

              <div className='mt-5'>
                <Text text={getContent('financial_point_one')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
                <Text text={getContent('financial_point_two')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
                <Text text={getContent('financial_point_three')} isTruncate={false} weight={TextWeight.regular} className={'block'} />
              </div>

              <div className='w-full h-0.5 bg-border mt-3 sm:mt-5'></div>

              <RowView className='gap-3 sm:gap-7 pt-3 sm:pt-5'>
                <Button
                  text={getContent('save_proceed')}
                  color={'text-white hover:text-primary '}
                  bgColor='bg-primary hover:bg-transparent hover:border-primary'
                  className={'border border-transparent w-44 px-0 text-center'}
                  type={'submit'}
                  loading={ind_post_loading}
                  isDisabled={ind_post_loading || values?.doYouHaveAnExistingPortfolio?.toString() === '1' && (totalIdeal !== 100 || totalCurrent !== 100)}
                  onClick={() => {
                    setFieldValue('isSaveExist', false);
                    handleSubmit();
                  }}
                />
                <Button
                  text={getContent('save_exit')}
                  color={'text-primary hover:text-white'}
                  bgColor='bg-white hover:bg-primary'
                  className={'border border-primary w-44 px-0 text-center'}
                  isDisabled={ind_post_loading}
                  onClick={() => {
                    setFieldValue('isSaveExist', true);
                    handleSubmit();
                  }}
                />
              </RowView>
            </div>
          );
        }}
      </Formik>
    </>
  );
};

export default InvestmentInformation;
